/**
 * Waterfall Chart Component
 *
 * Shows the journey from Revenue to Net Income with selectable items.
 * Only shows items that are in the selectedMetrics set.
 *
 * Styled for Monutrack dark theme with Monumenta Green (#D2FA52) as primary accent.
 */

// Waterfall item definition
export interface WaterfallItemConfig {
  key: string;
  label: string;
  type: 'start' | 'add' | 'subtract' | 'total';
}

// All waterfall items in order
export const WATERFALL_ITEMS: WaterfallItemConfig[] = [
  { key: 'faturamento', label: 'Faturamento', type: 'start' },
  { key: 'receitaBruta', label: 'Receita Bruta', type: 'start' },
  { key: 'receitaFinanceira', label: 'Rec. Financeira', type: 'add' },
  { key: 'impostos', label: 'Impostos', type: 'subtract' },
  { key: 'folha', label: 'Folha', type: 'subtract' },
  { key: 'custosExFolha', label: 'Custos (-Folha)', type: 'subtract' },
  { key: 'despesasOperacionais', label: 'Desp. Operac.', type: 'subtract' },
  { key: 'lucroLiquido', label: 'Lucro Líquido', type: 'total' },
];

interface WaterfallChartProps {
  values: Record<string, number>;
  selectedMetrics: Set<string>;
  height?: number;
}

// Format currency in compact form (Brazilian Real)
function formatCompactCurrency(value: number): string {
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toFixed(1)}M`;
  }
  if (absValue >= 1_000) {
    return `R$ ${(value / 1_000).toFixed(0)}K`;
  }
  return `R$ ${value.toFixed(0)}`;
}

interface ProcessedWaterfallItem extends WaterfallItemConfig {
  value: number;
  barStart: number;
  barEnd: number;
  runningTotal: number;
}

export function WaterfallChart({
  values,
  selectedMetrics,
  height = 260,
}: WaterfallChartProps): JSX.Element {
  // Filter to only selected items, preserving order
  const activeItems = WATERFALL_ITEMS.filter(item => selectedMetrics.has(item.key));

  if (activeItems.length === 0) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: '#9ca3af',
        background: '#1f2937',
        borderRadius: '8px',
      }}>
        Selecione métricas para exibir no waterfall
      </div>
    );
  }

  // Build processed items with running totals
  let runningTotal = 0;
  const processedItems: ProcessedWaterfallItem[] = activeItems.map((item) => {
    const rawValue = values[item.key] || 0;
    // For subtract items, ensure value is negative for calculation
    const value = item.type === 'subtract' ? -Math.abs(rawValue) : rawValue;

    if (item.type === 'start') {
      runningTotal = Math.abs(rawValue);
      return {
        ...item,
        value: Math.abs(rawValue),
        barStart: 0,
        barEnd: Math.abs(rawValue),
        runningTotal: Math.abs(rawValue),
      };
    }

    if (item.type === 'total') {
      return {
        ...item,
        value: rawValue,
        barStart: 0,
        barEnd: rawValue,
        runningTotal: rawValue,
      };
    }

    const prevTotal = runningTotal;
    runningTotal += value;

    return {
      ...item,
      value,
      barStart: value >= 0 ? prevTotal : runningTotal,
      barEnd: value >= 0 ? runningTotal : prevTotal,
      runningTotal,
    };
  });

  // Calculate scale
  const allValues = processedItems.flatMap(item => [item.barStart, item.barEnd]);
  const maxValue = Math.max(...allValues, 1);
  const minValue = Math.min(...allValues, 0);
  const range = maxValue - minValue || 1;

  // Chart dimensions
  const paddingLeft = 10;
  const paddingRight = 10;
  const paddingTop = 30;
  const paddingBottom = 50;
  const chartWidth = Math.max(processedItems.length * 90 + paddingLeft + paddingRight, 400);
  const chartHeight = height - paddingTop - paddingBottom;

  const barWidth = Math.min(60, (chartWidth - paddingLeft - paddingRight) / processedItems.length - 20);
  const xStep = (chartWidth - paddingLeft - paddingRight) / processedItems.length;
  const getX = (i: number): number => paddingLeft + i * xStep + xStep / 2;
  const getY = (val: number): number => paddingTop + chartHeight - ((val - minValue) / range) * chartHeight;

  // Get bar color based on type and value - dark theme colors
  const getBarColor = (item: ProcessedWaterfallItem): string => {
    if (item.type === 'start') return '#D2FA52';   // Monumenta Green
    if (item.type === 'total') return item.value >= 0 ? '#4ade80' : '#ef4444'; // green-400 or red-500
    if (item.value >= 0) return '#4ade80';         // green-400 (additions)
    return '#ef4444';                               // red-500 (deductions)
  };

  return (
    <div style={{
      background: '#1f2937',
      borderRadius: '12px',
      padding: '1rem',
      overflowX: 'auto',
    }}>
      <svg width={chartWidth} height={height} style={{ display: 'block' }}>
        {/* Zero line */}
        <line
          x1={paddingLeft}
          y1={getY(0)}
          x2={chartWidth - paddingRight}
          y2={getY(0)}
          stroke="#6b7280"
          strokeWidth="1"
        />

        {/* Connecting lines between bars */}
        {processedItems.map((item, i) => {
          if (i === 0 || item.type === 'total') return null;
          const prevItem = processedItems[i - 1];

          return (
            <line
              key={`connector-${i}`}
              x1={getX(i - 1) + barWidth / 2}
              y1={getY(prevItem.runningTotal)}
              x2={getX(i) - barWidth / 2}
              y2={getY(prevItem.runningTotal)}
              stroke="#6b7280"
              strokeWidth="1"
              strokeDasharray="4"
            />
          );
        })}

        {/* Bars */}
        {processedItems.map((item, i) => {
          const x = getX(i) - barWidth / 2;
          const y1 = getY(item.barStart);
          const y2 = getY(item.barEnd);
          const barHeight = Math.abs(y2 - y1);
          const barTop = Math.min(y1, y2);

          return (
            <g key={item.key}>
              {/* Bar */}
              <rect
                x={x}
                y={barTop}
                width={barWidth}
                height={Math.max(barHeight, 2)}
                fill={getBarColor(item)}
                rx="3"
              />

              {/* Value label */}
              <text
                x={x + barWidth / 2}
                y={barTop - 6}
                textAnchor="middle"
                fontSize="10"
                fontWeight="600"
                fill={getBarColor(item)}
              >
                {formatCompactCurrency(Math.abs(item.value))}
              </text>

              {/* X-axis label */}
              <text
                x={x + barWidth / 2}
                y={height - 15}
                textAnchor="middle"
                fontSize="9"
                fill="#9ca3af"
              >
                {item.label}
              </text>

              {/* +/- indicator */}
              {item.type !== 'start' && item.type !== 'total' && (
                <text
                  x={x + barWidth / 2}
                  y={height - 28}
                  textAnchor="middle"
                  fontSize="10"
                  fill={item.value >= 0 ? '#4ade80' : '#ef4444'}
                  fontWeight="600"
                >
                  {item.value >= 0 ? '(+)' : '(-)'}
                </text>
              )}
            </g>
          );
        })}

        {/* Legend - dark theme */}
        <g>
          <rect x={chartWidth - 100} y={5} width={10} height={10} fill="#D2FA52" rx="2" />
          <text x={chartWidth - 85} y={14} fontSize="9" fill="#9ca3af">Início</text>

          <rect x={chartWidth - 100} y={20} width={10} height={10} fill="#4ade80" rx="2" />
          <text x={chartWidth - 85} y={29} fontSize="9" fill="#9ca3af">Adição</text>

          <rect x={chartWidth - 100} y={35} width={10} height={10} fill="#ef4444" rx="2" />
          <text x={chartWidth - 85} y={44} fontSize="9" fill="#9ca3af">Dedução</text>

          <rect x={chartWidth - 100} y={50} width={10} height={10} fill="#4ade80" rx="2" />
          <text x={chartWidth - 85} y={59} fontSize="9" fill="#9ca3af">Total</text>
        </g>
      </svg>
    </div>
  );
}

export default WaterfallChart;
